import React from 'react';
import { CommonUtils, ProjectDetail } from './commonUtils';

// 공통 타입 정의
export interface FormData {
  brandCode?: string;
  code: string;
  status: string;
  type: string;
  title: string;
  subtitle?: string;
  description?: string;
}

export interface FormErrors {
  brandCode?: string;
  code?: string;
  status?: string;
  type?: string;
  title?: string;
}

// 공통 유틸리티 함수들
export class CategoryGroupFormUtils {
  // 코드 허용 문자: 영문 대소문자, 숫자, 언더스코어(_), 하이픈(-)
  static readonly CODE_PATTERN = /^[a-zA-Z0-9_-]*$/;

  // 기본 폼 데이터
  static getDefaultFormData(): FormData {
    return {
      brandCode: '',
      code: '',
      status: 'ACTIVE',
      type: '',
      title: '',
      subtitle: '',
      description: ''
    };
  }

  // 입력 변경 처리
  static handleInputChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    setFormData: React.Dispatch<React.SetStateAction<FormData>>,
    setErrors: React.Dispatch<React.SetStateAction<FormErrors>>
  ) {
    const { name, value } = e.target;
    // 코드: 영문 대소문자, 숫자, 언더스코어(_), 하이픈(-)만 허용
    const nextValue = name === 'code' ? String(value).replace(/[^a-zA-Z0-9_-]/g, '') : value;
    setFormData(prev => ({
      ...prev,
      [name]: nextValue
    }));
    // Clear error for this field
    if (setErrors) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name as keyof FormErrors];
        return newErrors;
      });
    }
  }

  // 폼 검증 함수
  static validateForm(formData: FormData): FormErrors {
    const newErrors: FormErrors = {};
    
    if (!formData.code.trim()) {
      newErrors.code = '코드는 필수입니다';
    } else if (!CategoryGroupFormUtils.CODE_PATTERN.test(formData.code)) {
      newErrors.code = '코드는 영문 대소문자, 숫자, 언더스코어(_), 하이픈(-)만 사용할 수 있습니다.';
    }
    
    if (!formData.status) {
      newErrors.status = '상태는 필수입니다';
    }
    
    if (!formData.type.trim()) {
      newErrors.type = '타입은 필수입니다';
    }
    
    if (!formData.title.trim()) {
      newErrors.title = '명칭은 필수입니다';
    }
    
    return newErrors;
  }

  // 수정 폼 검증 함수
  static validateUpdateForm(formData: FormData): FormErrors {
    return CategoryGroupFormUtils.validateForm(formData);
  }

  // 프로젝트 상세 정보 로드
  static loadProjectDetail(): ProjectDetail | null {
    return CommonUtils.loadProjectDetail();
  }

  // 모바일 사이드바 리사이즈 처리
  static useMobileSidebarResize(setIsMobileSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>) {
    return CommonUtils.useMobileSidebarResize(setIsMobileSidebarOpen);
  }

  // 상태 배지 정보 가져오기
  static getStatusBadge(status: string) {
    return CommonUtils.getStatusBadge(status, 'shop');
  }

  // 폼 제출 처리 (카테고리/그룹 생성)
  static async handleCreateSubmit(
    e: React.FormEvent,
    formData: FormData,
    validateForm: () => boolean,
    setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
    setSubmitError: React.Dispatch<React.SetStateAction<string | null>>,
    getProjectCode: () => string,
    createCategoryGroup: (projectCode: string, data: any) => Promise<any>,
    navigate: any
  ) {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const projectCode = getProjectCode();
      if (!projectCode) {
        throw new Error('프로젝트 코드가 없습니다.');
      }

      const categoryGroupData = {
        brandCode: formData.brandCode || undefined,
        code: formData.code,
        status: formData.status,
        type: formData.type,
        title: formData.title,
        subtitle: formData.subtitle || undefined,
        description: formData.description || undefined
      };

      const result = await createCategoryGroup(projectCode, categoryGroupData);
      
      if (result.success) {
        alert('카테고리/그룹이 성공적으로 생성되었습니다.');
        navigate('/shops/category-groups');
      } else {
        setSubmitError(result.message || '카테고리/그룹 생성에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('카테고리/그룹 생성 오류:', error);
      // 서버에서 보낸 에러 메시지 우선 사용
      const displayMessage = error?.message || CommonUtils.getNetworkErrorMessage(error) || '카테고리/그룹 생성 중 오류가 발생했습니다.';
      setSubmitError(displayMessage);
    } finally {
      setIsSubmitting(false);
    }
  }

  // 폼 제출 처리 (카테고리/그룹 수정)
  static async handleUpdateSubmit(
    e: React.FormEvent,
    formData: FormData,
    validateForm: () => boolean,
    setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
    setSubmitError: React.Dispatch<React.SetStateAction<string | null>>,
    setHasChanges: React.Dispatch<React.SetStateAction<boolean>>,
    setOriginalData: React.Dispatch<React.SetStateAction<FormData | null>>,
    getProjectCode: () => string,
    categoryGroupCode: string,
    updateCategoryGroup: (projectCode: string, categoryGroupCode: string, data: any) => Promise<any>,
    navigate: any,
    location: any
  ) {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const projectCode = getProjectCode();
      if (!projectCode) {
        throw new Error('프로젝트 코드가 없습니다.');
      }

      const categoryGroupData = {
        brandCode: formData.brandCode || undefined,
        code: formData.code,
        status: formData.status,
        type: formData.type,
        title: formData.title,
        subtitle: formData.subtitle || undefined,
        description: formData.description || undefined
      };

      const result = await updateCategoryGroup(projectCode, categoryGroupCode, categoryGroupData);
      
      if (result.success) {
        setHasChanges(false);
        setOriginalData({...formData});
        // 카테고리/그룹 목록으로 이동 (페이징 정보 유지)
        const searchParams = new URLSearchParams(location.search);
        const page = searchParams.get('page') || '0';
        const size = searchParams.get('size') || '20';
        navigate(`/shops/category-groups?page=${page}&size=${size}`);
      } else {
        setSubmitError(result.message || '카테고리/그룹 수정에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('카테고리/그룹 수정 오류:', error);
      // 서버에서 보낸 에러 메시지 우선 사용
      const displayMessage = error?.message || CommonUtils.getNetworkErrorMessage(error) || '카테고리/그룹 수정 중 오류가 발생했습니다.';
      setSubmitError(displayMessage);
    } finally {
      setIsSubmitting(false);
    }
  }

  // 정렬 핸들러
  static handleSort(
    field: string,
    sortField: string,
    sortOrder: string,
    setSortField: React.Dispatch<React.SetStateAction<string>>,
    setSortOrder: React.Dispatch<React.SetStateAction<string>>
  ) {
    return CommonUtils.handleSort(field, sortField, sortOrder, setSortField, setSortOrder);
  }

  // 전체 선택 핸들러
  static handleSelectAll<T>(
    selectedGroups: string[],
    filteredGroups: T[],
    setSelectedGroups: React.Dispatch<React.SetStateAction<string[]>>
  ) {
    return CommonUtils.handleSelectAll(selectedGroups, filteredGroups, setSelectedGroups, (group: T) => (group as any).code);
  }

  // 그룹 선택 핸들러
  static handleSelectItem(
    groupCode: string,
    selectedGroups: string[],
    setSelectedGroups: React.Dispatch<React.SetStateAction<string[]>>
  ) {
    return CommonUtils.handleSelectItem(groupCode, selectedGroups, setSelectedGroups);
  }

  // 페이지 변경 핸들러 (URL 업데이트 포함)
  static handlePageChange(
    page: number,
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>,
    location: any,
    navigate: any
  ) {
    setCurrentPage(page);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', page.toString());
    navigate({ search: searchParams.toString() }, { replace: true });
  }

  // 페이지 크기 변경 핸들러 (URL 업데이트 포함)
  static handlePageSizeChange(
    size: number,
    setPageSize: React.Dispatch<React.SetStateAction<number>>,
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>,
    location: any,
    navigate: any
  ) {
    setPageSize(size);
    setCurrentPage(0);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('size', size.toString());
    searchParams.set('page', '0');
    navigate({ search: searchParams.toString() }, { replace: true });
  }
}